import React, { useState } from 'react';
import { FaTrash, FaPencilAlt, FaTag, FaCopy } from 'react-icons/fa';
import { format } from 'date-fns';
import { CURRENCY_SYMBOLS } from '../utils/currencySymbols';

const StickyNote = ({ expense, onDelete, onEdit, onCategoryClick, onDuplicate }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    onDelete(expense.id);
    setShowDeleteModal(false);
  };

  const isPending = expense.isRecurring;

  return (
    <>
      <div className={`
        ${expense.color} 
        rounded-lg p-3 sm:p-4 
        shadow-md h-48 sm:h-64 
        flex flex-col 
        relative group
        dark:text-gray-800
        ${isPending ? 'border-2 border-teal-500 dark:border-pink-500' : ''}
      `}>
        {/* Pending Badge */}
        {isPending && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-teal-500 dark:bg-pink-500 text-white px-3 pt-0.5 pb-1.5 rounded-full text-xs font-semibold shadow-sm">
            Upcoming
          </div>
        )}

        {/* Action buttons - always visible on mobile, hover on desktop */}
        <div className="absolute bottom-2 right-2 flex gap-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          {!isPending && (
            <>
              <button 
                onClick={() => onEdit(expense)}
                className="p-1 sm:p-1.5 bg-white rounded-full shadow hover:bg-gray-50 text-teal-600"
              >
                <FaPencilAlt className="p-0.5 sm:p-1 h-5 w-5 sm:h-6 sm:w-6"/>
              </button>
              <button 
                onClick={() => onDuplicate(expense)}
                className="p-1 sm:p-1.5 bg-white rounded-full shadow hover:bg-gray-50 text-blue-600"
              >
                <FaCopy className="p-0.5 sm:p-1 h-5 w-5 sm:h-6 sm:w-6"/>
              </button>
              <button 
                onClick={handleDelete}
                className="p-1 sm:p-1.5 bg-white rounded-full shadow hover:bg-gray-50 text-red-500"
              >
                <FaTrash className="p-0.5 sm:p-1 h-5 w-5 sm:h-6 sm:w-6"/>
              </button>
            </>
          )}
        </div>

        <div className="font-semibold text-base sm:text-lg mb-1 sm:mb-2 dark:text-gray-800">
          {expense.title}
        </div>
        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-700 mb-1 sm:mb-2">
          {CURRENCY_SYMBOLS[expense.currency]}{expense.amount} • 
          <button 
            onClick={() => onCategoryClick(expense.category)}
            className="inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-800 ml-1"
          >
            <FaTag size={8} className="sm:w-2.5 sm:h-2.5" />
            {expense.category}
          </button>
        </div>
        <div 
          className="flex-1 overflow-y-auto text-xs sm:text-sm prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: expense.description }}
        />

        {/* Recurring Info */}
        {isPending && (
          <div className="mt-2 text-xs text-teal-600 dark:text-pink-600 font-medium border-t border-teal-200 dark:border-pink-200 pt-2">
            {expense.isOneOff ? (
              <>One-off payment • {format(new Date(expense.nextPaymentDate), 'd MMM yyyy')}</>
            ) : (
              <>
                Every {expense.period.number} {expense.period.unit}
                {expense.endDate && ` • Ends ${format(new Date(expense.endDate), 'd MMM yyyy')}`}
              </>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && !isPending && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Expense</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this expense?</p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:hover:bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StickyNote;